import { Component } from 'react';
import { ANIMATION_DURATION, handleRefSplit, Position } from '@optimus-web/common';
import { ClickAwayProps, StyledTransitionGroup, withClickAway } from '@optimus-web/helpers';
import { disableScroll } from '../utils/dom';
import { SidebarDefaultedProps, SidebarProps } from './SidebarProps';
import { BackdropElement, SidebarContainerElement, SidebarElement } from './style';

class SidebarComponent extends Component<SidebarProps & ClickAwayProps> {
    private sidebarElement?: HTMLElement | null;
    private enableScroll?: (() => void) | null;

    static defaultProps = {
        disableBodyScroll: true,
        clickAwayRoots: [],
        modal: true,
        disableClickAway: false,
        position: Position.Right as const,
        width: '50vw',
        closeOnBackdropClick: false,
    };

    componentDidMount() {
        if (this.props.opened) {
            this.open();
        }
    }

    componentDidUpdate(prevProps: SidebarProps) {
        if (!prevProps.opened && this.props.opened) {
            this.open();
        }

        if (prevProps.opened && !this.props.opened) {
            this.close();
        }
    }

    componentWillUnmount() {
        if (this.props.opened) {
            this.close();
        }
    }

    handleBackdropClick = () => {
        if (this.props.closeOnBackdropClick && this.props.onClose) {
            this.props.onClose();
        }
    };

    handleClickAway = () => {
        if (!this.props.modal && this.props.onClose) {
            this.props.onClose();
        }
    };

    open() {
        if (!this.props.disableClickAway) {
            this.props.registerClickAway({
                handler: this.handleClickAway,
                roots: [...this.props.clickAwayRoots, () => this.sidebarElement],
            });
        }

        if (this.props.disableBodyScroll) {
            this.enableScroll = disableScroll(document.body);
        }
    }

    close() {
        if (!this.props.disableClickAway) {
            this.props.deregisterClickAway();
        }

        if (this.enableScroll) {
            this.enableScroll();
            this.enableScroll = null;
        }
    }

    handleSidebarRef = (component: HTMLDivElement | null) => {
        this.sidebarElement = component;
    };

    render() {
        const {
            opened,
            width,
            modal,
            children,
            className,
            position,
            onClosingAnimationFinished,
            forwardedRef,
            absolute,
        } = this.props;

        const containerElementProps = {
            ...StyledTransitionGroup.defaultProps,
            className,
            childrenProps: {
                opened,
            },
            timeoutExit: ANIMATION_DURATION.default,
            absolute,
        };

        const backdropElementProps = {
            opened,
            key: 'backdrop',
            onClick: this.handleBackdropClick,
        };

        const sidebarElementProps = {
            ref: handleRefSplit({ ownRef: this.handleSidebarRef, forwardedRef }),
            key: 'sidebar',
            width,
            position,
            children,
            onClosingAnimationFinished,
            opened,
            disableScroll: this.props.disableScroll,
        };

        return (
            <SidebarContainerElement data-test-id="sidebar" {...containerElementProps}>
                {opened && modal && <BackdropElement {...backdropElementProps} />}
                {opened && <SidebarElement {...sidebarElementProps} data-test-sidebar-element />}
            </SidebarContainerElement>
        );
    }
}

/**
 * @deprecated Use new Menu and SidePanel components instead
 */
export const Sidebar = withClickAway(SidebarComponent) as React.ComponentType<SidebarDefaultedProps>;
